import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth-store'
import { BannedAccountScreen } from '@/components/auth/banned-account-screen'
import { profileNeedsCompletion } from '@/lib/profile-onboarding'
import { Loader2 } from 'lucide-react'

interface ProtectedRouteProps {
  requireAuth?: boolean
  requireRole?: 'admin' | 'moderator'
  children?: React.ReactNode
}

export function ProtectedRoute({ requireAuth = false, requireRole, children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, role, user } = useAuthStore()
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/auth" state={{ from: location }} replace />
  }

  if (requireAuth && isAuthenticated && user?.is_banned) {
    return <BannedAccountScreen />
  }

  if (
    requireAuth &&
    isAuthenticated &&
    profileNeedsCompletion(user) &&
    location.pathname !== '/onboarding/profile'
  ) {
    return <Navigate to="/onboarding/profile" replace state={{ from: location }} />
  }

  if (requireRole) {
    const hasRequiredRole = requireRole === 'admin' 
      ? role === 'admin' || role === 'moderator'
      : role === requireRole
    
    if (!hasRequiredRole) {
      return <Navigate to="/" replace />
    }
  }

  return children ? <>{children}</> : <Outlet />
}

interface AdminRouteProps {
  children?: React.ReactNode
}

export function AdminRoute({ children }: AdminRouteProps) {
  const { isAuthenticated, isLoading, role, user } = useAuthStore()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />
  }

  if (profileNeedsCompletion(user)) {
    return <Navigate to="/onboarding/profile" replace />
  }

  if (user?.is_banned) {
    return <BannedAccountScreen />
  }

  if (role !== 'admin' && role !== 'moderator') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <h1 className="text-2xl font-bold">Access Denied</h1>
        <p className="text-muted-foreground">You don't have permission to access this page.</p>
      </div>
    )
  }

  return <>{children || <Outlet />}</>
}